/**
 * Account Payment History Page
 * Shows current subscription plan with next payment details
 * and full payment history with invoice links
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, Calendar, FileText, ExternalLink, 
  ChevronRight, Loader2, AlertCircle, Receipt
} from 'lucide-react';
import AccountHeader from '../../components/account/AccountHeader';
import { useAccountAuth } from '../../contexts/AccountAuthContext';
import { API_BASE_URL } from '../../config/api';

const AccountPaymentHistory = () => {
  const navigate = useNavigate();
  const { subscription } = useAccountAuth();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

  const email = sessionStorage.getItem('account_email') || '';
  const pwd = sessionStorage.getItem('account_pwd') || '';

  useEffect(() => {
    fetchPaymentHistory(1);
  }, []);

  const fetchPaymentHistory = async (page) => {
    try {
      if (page === 1) setIsLoading(true);
      else setLoadingMore(true);
      
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'payment_history',
          email,
          pwd,
          page
        })
      });

      const result = await response.json();
      
      if (result.success) {
        if (page === 1) {
          setInvoices(result.data.invoices);
        } else {
          setInvoices(prev => [...prev, ...result.data.invoices]);
        }
        setSummary(result.data.summary);
        setPagination(result.data.pagination);
        setCurrentPage(page);
      } else {
        setError(result.message || 'Failed to load payment history');
      }
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError('Failed to load payment history');
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    setLoadingInvoiceId(invoice.iid);
    try {
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'invoice_url',
          email,
          pwd,
          invoice_id: invoice.iid
        })
      });
      const result = await response.json();
      const url = result.data?.url || result.data?.pdf || invoice.pdf;
      if (url) {
        window.open(url, '_blank');
      } else {
        alert('Invoice document is not available yet.');
      }
    } catch (err) {
      // Fallback to stored URL
      if (invoice.pdf) {
        window.open(invoice.pdf, '_blank');
      } else {
        alert('Could not open invoice.');
      }
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPeriod = (start, end) => {
    if (!start || !end) return '—';
    const s = new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const e = new Date(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} – ${e}`;
  };

  const formatCardInfo = (pm) => {
    if (!pm) return '—';
    // Extract last 4 digits from payment_method string like "card_****4242"
    const match = pm.match(/(\d{4})$/);
    return match ? `•••• ${match[1]}` : pm;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-emerald-400';
      case 'pending': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      case 'refunded': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-400/10';
      case 'pending': return 'bg-yellow-400/10';
      case 'failed': return 'bg-red-400/10';
      case 'refunded': return 'bg-blue-400/10';
      default: return 'bg-gray-400/10';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark">
        <AccountHeader title="Payment History" />
        <div className="flex items-center justify-center p-16">
          <Loader2 className="w-8 h-8 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="Payment History" />
      
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        
        {/* Current Plan & Next Payment */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-dark-100 rounded-2xl p-5 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Current Plan</h3>
                <p className="text-sm text-accent">{summary.cp}</p>
              </div>
            </div>

            <div className="h-px bg-white/10 mb-4" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Next Payment Date</p>
                <p className="text-white font-medium">{summary.nbd ? formatDate(summary.nbd) : 'No upcoming payment'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Next Amount</p>
                <p className="text-white font-medium">
                  {summary.nba > 0 ? `$${summary.nba.toFixed(2)}` : '$0.00'}
                </p>
              </div>
            </div>

            {summary.ti > 0 && (
              <div className="mt-3 pt-3 border-t border-white/5">
                <p className="text-xs text-gray-500">
                  {summary.ti} total {summary.ti === 1 ? 'payment' : 'payments'} on record
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Payment History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">Payment History</h3>

          {invoices.length === 0 ? (
            <div className="bg-dark-100 rounded-2xl p-8 border border-white/10 text-center">
              <Receipt className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No payments yet</p>
              <p className="text-sm text-gray-600 mt-1">Your payment history will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice, index) => (
                <motion.div
                  key={invoice.iid || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className="bg-dark-100 rounded-xl border border-white/10 overflow-hidden"
                >
                  {/* Invoice Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-white font-medium">{invoice.pn || 'Subscription'}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getStatusBg(invoice.st)} ${getStatusColor(invoice.st)}`}>
                            {invoice.st}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{invoice.desc}</p>
                      </div>
                      <p className="text-white font-semibold text-lg">
                        ${(invoice.amt || 0).toFixed(2)}
                      </p>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-gray-600" />
                        <div>
                          <p className="text-[11px] text-gray-600">Period</p>
                          <p className="text-gray-400">{formatPeriod(invoice.ps, invoice.pe)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-gray-600" />
                        <div>
                          <p className="text-[11px] text-gray-600">Payment Date</p>
                          <p className="text-gray-400">{formatDate(invoice.pa || invoice.ca)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-3.5 h-3.5 text-gray-600" />
                        <div>
                          <p className="text-[11px] text-gray-600">Card</p>
                          <p className="text-gray-400">{formatCardInfo(invoice.pm)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-gray-600" />
                        <div>
                          <p className="text-[11px] text-gray-600">Invoice #</p>
                          <p className="text-gray-400">{invoice.inum || '—'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View Invoice Link */}
                  {invoice.sid && (
                    <button
                      onClick={() => handleViewInvoice(invoice)}
                      disabled={loadingInvoiceId === invoice.iid}
                      className="flex items-center justify-center gap-2 py-3 border-t border-white/5 text-accent hover:bg-accent/5 transition-colors w-full disabled:opacity-50"
                    >
                      {loadingInvoiceId === invoice.iid ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4" />
                      )}
                      <span className="text-sm font-medium">
                        {loadingInvoiceId === invoice.iid ? 'Loading...' : 'View Invoice'}
                      </span>
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Load More */}
          {pagination?.has_more && (
            <button
              onClick={() => fetchPaymentHistory(currentPage + 1)}
              disabled={loadingMore}
              className="w-full mt-4 py-3 bg-dark-100 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all flex items-center justify-center gap-2"
            >
              {loadingMore ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <span className="text-sm">Load More</span>
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AccountPaymentHistory;
